import { useEffect, useState } from "react";
import platformApi from "@/lib/platformApi";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileDown, Link2 } from "lucide-react";

const BROCHURE_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/marketing/brochure.pdf`;
const CHANNELS = ["linkedin", "twitter", "email", "whatsapp"];

// mirrors the backend slug rules: lowercase [a-z0-9_-], 32-char cap
const slugify = (s) => (s || "").trim().toLowerCase().replace(/[^a-z0-9_-]+/g, "").slice(0, 32);

const trackedUrl = (slug) => (slug ? `${BROCHURE_BASE}?src=${slug}` : BROCHURE_BASE);

async function copyLink(slug) {
  const url = trackedUrl(slug);
  try {
    await navigator.clipboard.writeText(url);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = url;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  }
  toast.success(`Tracked link copied — logs as src "${slug || "direct"}"`);
}

export default function BrochureStatsCard() {
  const [stats, setStats] = useState(null);
  const [campaign, setCampaign] = useState("");

  useEffect(() => {
    platformApi.get("/marketing/brochure-stats")
      .then((r) => setStats(r.data))
      .catch(() => setStats({ total: 0, by_src: [], window_days: 30 }));
  }, []);

  if (!stats) return <Skeleton className="h-32 w-full rounded-none" />;

  const slug = slugify(campaign);

  return (
    <div className="bg-white border border-slate-200 p-5 max-w-md" data-testid="brochure-stats-card">
      <div className="flex items-center justify-between">
        <h3 className="text-[12px] font-bold uppercase tracking-[0.14em] text-[#1E293B] flex items-center gap-2">
          <FileDown className="h-4 w-4" style={{ color: "var(--brand-accent-ink)" }} />
          Brochure downloads
        </h3>
        <span className="text-[9px] uppercase tracking-[0.14em] text-slate-400">last {stats.window_days} days</span>
      </div>
      <p className="text-[28px] font-bold font-mono text-[#1E293B] mt-2" data-testid="brochure-stats-total">
        {stats.total}
      </p>
      {stats.by_src.length > 0 && (
        <table className="w-full text-[11.5px] mt-3" data-testid="brochure-stats-by-src">
          <tbody className="divide-y divide-slate-100">
            {stats.by_src.map(({ src, count }) => (
              <tr key={src} data-testid={`brochure-src-${src}`}>
                <td className="py-1.5 font-mono text-slate-600">{src}</td>
                <td className="py-1.5 text-right font-mono font-bold text-[#1E293B]">{count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {stats.by_src.length === 0 && (
        <p className="text-[11px] text-slate-400 mt-2">No downloads recorded yet.</p>
      )}

      <div className="border-t border-slate-200 mt-4 pt-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 flex items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5" style={{ color: "var(--brand-accent-ink)" }} /> Copy tracked link
        </p>
        <div className="flex gap-2 mt-2.5">
          <Input data-testid="brochure-campaign-input" placeholder="Campaign name (e.g. Q3 Expo)"
            value={campaign} onChange={(e) => setCampaign(e.target.value)}
            className="rounded-none h-8 text-[12px]" maxLength={64} />
          <button data-testid="brochure-copy-btn" onClick={() => copyLink(slug)}
            className="shrink-0 h-8 px-3 bg-[#1E293B] hover:bg-slate-700 text-white text-[10px] font-bold uppercase tracking-[0.12em] transition-colors">
            Copy link
          </button>
        </div>
        <p className="text-[10px] font-mono text-slate-400 mt-2 break-all" data-testid="brochure-link-preview">
          {trackedUrl(slug)}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {CHANNELS.map((c) => (
            <button key={c} data-testid={`brochure-chip-${c}`} onClick={() => copyLink(c)}
              className="text-[10px] font-mono px-2 py-1 border border-slate-200 text-slate-600 hover:border-slate-400 hover:text-[#1E293B] transition-colors">
              {c}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
